import React, {Fragment, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, HStack, useDisclosure, useStyleConfig} from '@chakra-ui/react'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import AlternativeSwatchGroup from '../swatch-group/alternative-swatch-group'
import ProductThumbnail from '../product-thumbnail'
import {FormattedMessage, useIntl} from 'react-intl'
import {selectablesValuesFiltered} from '../../custom-hooks/use-custom-variation-group'
import {remCalc} from '../../theme/_utils'
import {
    PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID,
    PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID,
} from '../../constants'
import {BRAND_KEY} from '../../brand'
import ProductVariantsSelector from './product-variants-selector'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {isServer} from '../../utils/utils'
import {getPriceData} from '../../utils/product-utils'
import useHexColor from '../../hooks/use-hex-color'

const getSwatchType = (swatch) => {
    if (!swatch) return ''

    return swatch?.swatchType || swatch?.swatchtype || swatch?.type || swatch?.swatch_type || ''
}

const normalizeComparable = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()

const findVariationGroupByValue = (variationGroups = [], value) => {
    const target = normalizeComparable(value)

    if (target) {
        const found = variationGroups.find((item) => {
            return (
                normalizeComparable(item?.productId) === target ||
                normalizeComparable(item?.id) === target ||
                normalizeComparable(item?.swatch?.subModelIdentifier) === target
            )
        })

        if (found) return found
    }

    return null
}

function ProductVariants({
    variationAttributes,
    product,
    lastItemThreshold,
    InfoStockIcon,
    OutOfStockIcon,
    LastFewItemsVariant,
    OutOfStockVariant,
    priceData,
    activeCurrency,
}) {
    const intl = useIntl()
    const styles = useStyleConfig('ProductVariants', {})
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {resolveHexColor: resolveVariantHexColor, getHexFromObject} = useHexColor()

    const ids = product?.c_enabledVariationGroups?.map(({productId}) => productId)
    const {data: variantsInSwatch} = useProducts(
        {
            parameters: {
                ids: ids?.join(','),
                expand: ['availability'],
                select: '(data.(id,c_variants,type,price,tieredPrices,c_hexColor,c_hexColour,hexColor,hexColour,c_marketingColour))',
            },
            headers: {
                'x-debug': 'product-view-index',
            },
        },
        {
            enabled: !isServer && ids?.length > 0,
            keepPreviousData: true,
        },
    )

    const mapping = useMemo(() => {
        return (
            variantsInSwatch?.data?.reduce((acc, item) => {
                if (item.c_variants && item.c_variants.length > 0) {
                    acc[item.id] = {
                        pid: item.c_variants[0].productId,
                        priceData: getPriceData(item),
                        hexColor:
                            item.c_hexColor || item.c_hexColour || item.hexColor || item.hexColour,
                        marketingColor: item.c_marketingColour,
                    }
                }
                return acc
            }, {}) || {}
        )
    }, [variantsInSwatch?.data])

    const swatchPayload = useMemo(() => {
        const groups = product?.c_enabledVariationGroups || []
        const alternativeGroup = groups.find(
            (group) => getSwatchType(group?.swatch).toLowerCase() === 'alternative',
        )

        if (alternativeGroup?.swatch) return alternativeGroup.swatch

        const swatchGroupWithPayload = groups.find((group) => group?.swatch)
        return swatchGroupWithPayload?.swatch ?? null
    }, [product?.c_enabledVariationGroups])

    const swatchType = getSwatchType(swatchPayload).toLowerCase()
    const isAlternativeSwatchType = swatchType === 'alternative'
    const enabledVariationGroups = product?.c_enabledVariationGroups || []

    // Determine which attribute to use for swatches
    // If AlternativeSwatch is defined, use that instead of default color attribute
    const swatchAttributeId = product?.c_alternativeSwatch || PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID

    return variationAttributes.map(({id, name, selectedValue, values = []}) => {
        if (id === PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID) return

        if (id === swatchAttributeId)
            values = selectablesValuesFiltered(values, enabledVariationGroups)

        if (BRAND_KEY === 'braun') {
            return (
                <ProductVariantsSelector
                    key={id}
                    styles={styles}
                    isOpen={isOpen}
                    onClose={onClose}
                    product={product}
                    activeCurrency={activeCurrency}
                    lastItemThreshold={lastItemThreshold}
                    priceData={priceData}
                    LastFewItemsVariant={LastFewItemsVariant}
                    OutOfStockVariant={OutOfStockVariant}
                    InfoStockIcon={InfoStockIcon}
                    OutOfStockIcon={OutOfStockIcon}
                    onOpen={onOpen}
                />
            )
        }

        const isAlternativeAttributeId =
            id === swatchAttributeId || id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID

        // New payload support: when swatch.swatchtype is 'alternative',
        // render tabs from c_enabledVariationGroups[].swatch.alternativeSwatchValue.
        if (isAlternativeAttributeId && isAlternativeSwatchType) {
            const alternativeGroups = enabledVariationGroups.filter(
                (variationGroup) =>
                    getSwatchType(variationGroup?.swatch).toLowerCase() === 'alternative',
            )

            const selectedVariantId =
                selectedValue?.value ||
                product?.c_subModelIdentifier ||
                product?.productId ||
                product?.id
            const alternativeFieldKey =
                swatchPayload?.alternativeSwatchField || product?.c_alternativeSwatch

            const options = alternativeGroups.map((variationGroup) => {
                const optionValue = variationGroup?.productId
                const optionLabel =
                    variationGroup?.swatch?.alternativeSwatchValue ||
                    variationGroup?.swatch?.[alternativeFieldKey] ||
                    variationGroup?.[alternativeFieldKey] ||
                    variationGroup?.[`c_${alternativeFieldKey}`] ||
                    variationGroup?.productId
                const mappingData = mapping?.[variationGroup?.productId]
                let optionHref = variationGroup?.slugUrl

                if (mappingData?.pid && optionHref) {
                    optionHref += `?pid=${mappingData.pid}`
                }

                return {
                    value: optionValue,
                    name: optionLabel,
                    href: optionHref,
                }
            })

            const selectedOption =
                options.find((option) => option.value === selectedVariantId) || options[0]
            const rawFieldLabel = alternativeFieldKey || name
            const fallbackFieldLabel = String(rawFieldLabel)
                .replace(/([A-Z])/g, ' $1')
                .replace(/_/g, ' ')
                .replace(/^\w/, (c) => c.toUpperCase())
                .trim()

            const translationKey = alternativeFieldKey
                ? `tech_spec_${alternativeFieldKey}`
                : undefined
            const translatedAlternativeLabel =
                translationKey && typeof intl?.messages?.[translationKey] === 'string'
                    ? intl.messages[translationKey]
                    : undefined
            const alternativeFieldLabel = translatedAlternativeLabel || fallbackFieldLabel

            return (
                <AlternativeSwatchGroup
                    key={id}
                    label={alternativeFieldLabel}
                    c_alternativeSwatch={alternativeFieldKey}
                    displayName={selectedOption?.name || ''}
                    value={selectedOption?.value || selectedVariantId}
                    options={options}
                />
            )
        }

        const swatchValues =
            id === swatchAttributeId && !isAlternativeSwatchType
                ? enabledVariationGroups
                      .filter(
                          (variationGroup) =>
                              Boolean(getSwatchType(variationGroup?.swatch)) ||
                              Boolean(
                                  variationGroup?.c_hexColor ||
                                      variationGroup?.c_hexColour ||
                                      variationGroup?.hexColor ||
                                      variationGroup?.hexColour,
                              ),
                      )
                      .map((variationGroup) => {
                          const mappingData = mapping?.[variationGroup?.productId]
                          const payloadHexColor = resolveVariantHexColor(
                              getHexFromObject(variationGroup?.swatch),
                              getHexFromObject(variationGroup),
                              variationGroup?.c_hexColor,
                              variationGroup?.c_hexColour,
                              variationGroup?.hexColor,
                              variationGroup?.hexColour,
                              mappingData?.hexColor,
                          )
                          const matchedValue = values.find((option) => {
                              return (
                                  option?.value === variationGroup?.productId ||
                                  option?.value === variationGroup?.swatch?.subModelIdentifier
                              )
                          })

                          return {
                              ...matchedValue,
                              variationGroup,
                              value:
                                  variationGroup?.productId ||
                                  variationGroup?.swatch?.subModelIdentifier ||
                                  matchedValue?.value,
                              name:
                                  matchedValue?.name ||
                                  variationGroup?.swatch?.title ||
                                  variationGroup?.swatch?.subModelIdentifier,
                              href: variationGroup?.slugUrl || matchedValue?.href,
                              image: matchedValue?.image,
                              orderable: matchedValue?.orderable,
                              mappingData,
                              swatchHexColor: payloadHexColor,
                          }
                      })
                : values

        const swatches = swatchValues.map((props, index) => {
            let {href, name, image, value, orderable, swatchHexColor} = props

            const variation =
                props?.variationGroup || findVariationGroupByValue(enabledVariationGroups, value)

            let variantPriceData = props?.mappingData?.priceData || null
            let hexColor = swatchHexColor
            if (variation) {
                const variationSwatch = variation?.swatch || {}

                href = variation?.slugUrl
                const mappingData = props?.mappingData || mapping?.[variation.productId]
                if (mappingData?.pid) {
                    href += '?pid=' + mappingData.pid
                }
                variantPriceData = variantPriceData || mappingData?.priceData

                if (getSwatchType(variationSwatch).toLowerCase() === 'alternative') {
                    name = variationSwatch?.alternativeSwatchValue || name
                }

                // Resolve color from value, variation group, then fetched variant product
                hexColor =
                    hexColor ||
                    resolveVariantHexColor(
                        variationSwatch?.hexColour,
                        variationSwatch?.hexColor,
                        variation?.c_hexColor,
                        variation?.c_hexColour,
                        variation?.hexColor,
                        variation?.hexColour,
                        mappingData?.hexColor,
                    )
            }

            const isCurrentProductVariation =
                value === product?.c_subModelIdentifier ||
                value === product?.id ||
                variation?.productId === product?.c_subModelIdentifier ||
                variation?.productId === product?.id

            if (!hexColor && isCurrentProductVariation) {
                hexColor = resolveVariantHexColor(
                    product?.c_hexColor,
                    product?.c_hexColour,
                    product?.hexColor,
                    product?.hexColour,
                )
            }

            // Determine if variant has a discount
            const variantHasDiscount =
                variantPriceData?.isOnSale &&
                priceData?.currentPrice &&
                variantPriceData?.pricePerUnit < priceData?.pricePerUnit

            const hasSelection = Boolean(selectedValue?.value)
            const isSelected = selectedValue?.value === value
            let content

            if (image) {
                content = (
                    <ProductThumbnail
                        handleSelection={() => {}}
                        selected={isSelected}
                        image={image}
                        variant="colorThumbnail"
                    />
                )
            } else {
                content = name
            }
            const isFirst = index === 0
            // To mimic the behavior of a native radio input, only
            // one swatch should receive tab focus; the rest can be
            // selected using arrow keys when the swatch group has
            // focus. The focused element is the selected option or
            // the first in the group, if no option is selected.
            // This is a slight difference, for simplicity, from the
            // native element, where the first element is focused on
            // `Tab` and the _last_ element is focused on `Shift+Tab`
            const isFocusable = isSelected || (!hasSelection && isFirst)

            return (
                <Swatch
                    key={value}
                    href={href}
                    disabled={orderable === false}
                    value={value}
                    name={name}
                    variant="square"
                    selected={isSelected}
                    isFocusable={isFocusable}
                    position={index + 1}
                    total={swatchValues?.length}
                    variantPriceData={variantPriceData}
                    hasDiscount={variantHasDiscount}
                    activeCurrency={activeCurrency}
                    hexColor={id === swatchAttributeId ? hexColor : null}
                >
                    {content}
                </Swatch>
            )
        })

        const singleSwatchHexColor = swatchValues?.[0]?.swatchHexColor
        const hasSingleColorSwatch = Boolean(singleSwatchHexColor)

        if (id === swatchAttributeId && swatches.length === 1 && !hasSingleColorSwatch) {
            if (selectedValue?.name?.toLowerCase() === 'undefined')
                return <Fragment key={id}></Fragment>

            return (
                <HStack
                    key={id}
                    color="text.default"
                    fontSize={remCalc(14)}
                    data-ab="variant-colors"
                    data-cs-label="variant-colors"
                >
                    <Box fontWeight="regular">
                        {id === swatchAttributeId ? (
                            <FormattedMessage id="variants.label.color" defaultMessage="Color" />
                        ) : (
                            name
                        )}
                        :
                    </Box>
                    <Box fontWeight="bold">{selectedValue?.displayValue || ''}</Box>
                </HStack>
            )
        }

        // For non-color attributes, render as tabs only when not using payload-driven
        // alternative swatch mode.
        if (!isAlternativeSwatchType && id !== swatchAttributeId) {
            return (
                <AlternativeSwatchGroup
                    key={id}
                    label={name}
                    c_alternativeSwatch={product?.c_alternativeSwatch}
                    displayName={selectedValue?.displayValue || ''}
                    value={selectedValue?.value}
                    options={values}
                />
            )
        }

        return (
            <SwatchGroup
                key={id}
                value={selectedValue?.value}
                displayName={selectedValue?.displayValue || ''}
                id={id}
                label={name}
            >
                {swatches}
            </SwatchGroup>
        )
    })
}

ProductVariants.propTypes = {
    variationAttributes: PropTypes.array,
    product: PropTypes.object,
    lastItemThreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    InfoStockIcon: PropTypes.elementType,
    OutOfStockIcon: PropTypes.elementType,
    LastFewItemsVariant: PropTypes.string,
    OutOfStockVariant: PropTypes.string,
    priceData: PropTypes.object,
    activeCurrency: PropTypes.string,
}

export default ProductVariants
